import { ContractorDetailsItem } from "@dune/types";
import {
	Button,
	Card,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@dune/ui";
import React, { useState } from "react";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
	useDeleteParticipant,
	useUpdateParticipant,
} from "../../../../../api/mutations/participants.mutations";
import { useMe } from "../../../../../api/queries/users.queries";
import { ContractorDetails } from "./informationsTabFiles/ContractorDetails";
import { useEditContractorForm } from "./informationsTabFiles/useEditContractorForm";
import { RoleEnum } from "../../../../../../../../packages/vars/src";

interface InformationsTabProps {
	contractor: ContractorDetailsItem;
}

const InformationsTab: React.FC<InformationsTabProps> = ({ contractor }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const { form } = useEditContractorForm(contractor);
	const { mutate: updateParticipant, isPending } = useUpdateParticipant();
	const { mutate: deleteParticipant, isPending: isDeleting } =
		useDeleteParticipant();
	const { me: user } = useMe();
	const isDuneUser = user?.role === RoleEnum.DUNE;
	const navigate = useNavigate();

	const onSubmit = form.handleSubmit((data) => {
		updateParticipant({
			id: contractor.id,
			data: {
				type: data.type,
				siren: data.siren,
				denomination: data.denomination,
				headquartersAddress: data.headquartersAddress,
			},
		});
	});

	const handleEditClick = () => {
		setIsEditing(true);
	};

	const handleCancelEdit = () => {
		setIsEditing(false);
		form.reset();
	};

	const handleDeleteClick = () => {
		setShowDeleteModal(true);
	};

	const handleCancelDelete = () => {
		setShowDeleteModal(false);
	};

	const handleConfirmDelete = async () => {
		deleteParticipant(contractor.id, {
			onSuccess: () => {
				navigate("/contractors");
			},
		});
		setShowDeleteModal(false);
	};

	return (
		<FormProvider {...form}>
			<div className="space-y-6">
				<ContractorDetails isEditing={isEditing} />

				{!isEditing ? (
					<div className="fixed bottom-8 right-8 z-50">
						<Card className="bg-white shadow-lg p-4">
							<div className="flex flex-col gap-2">
								<Button
									onClick={handleEditClick}
									variant="default"
									className="w-full min-w-[200px]"
								>
									Modifier la fiche
								</Button>
								{isDuneUser && (
									<Button
										onClick={handleDeleteClick}
										variant="outline"
										className="w-full min-w-[200px] text-red-600 border-red-600 hover:bg-red-50"
										disabled={isDeleting}
									>
										{isDeleting ? "Suppression..." : "Supprimer l'intervenant"}
									</Button>
								)}
							</div>
						</Card>
					</div>
				) : (
					<div className="fixed bottom-8 right-8 z-50">
						<Card className="bg-white shadow-lg p-4">
							<div className="flex gap-2">
								<Button
									onClick={handleCancelEdit}
									variant="outline"
									className="flex-1"
									disabled={isPending}
								>
									Annuler
								</Button>
								<Button
									onClick={onSubmit}
									variant="default"
									className="flex-1"
									disabled={isPending}
								>
									{isPending ? "Enregistrement..." : "Valider"}
								</Button>
							</div>
						</Card>
					</div>
				)}
			</div>

			<Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Confirmer la suppression</DialogTitle>
						<DialogDescription>
							Êtes-vous sûr de vouloir supprimer cet intervenant ? Cette action
							est irréversible.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={handleCancelDelete}
							disabled={isDeleting}
						>
							Annuler
						</Button>
						<Button
							variant="destructive"
							onClick={handleConfirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Suppression..." : "Supprimer"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</FormProvider>
	);
};

export default InformationsTab;
